<script setup lang="ts">
import { ref } from "vue";

const props = withDefaults(
  defineProps<{
    accept?: string;
    multiple?: boolean;
    maxSize?: number;
    disabled?: boolean;
    label?: string;
    errorMsg?: string;
  }>(),
  {
    label: "Drop files here or click to browse",
    multiple: false,
    disabled: false,
  }
);

const emit = defineEmits<{
  "update:files": [files: File[]];
  error: [reason: "size" | "type", file: File];
}>();

const isDragging = ref(false);
const files = ref<File[]>([]);
const inputRef = ref<HTMLInputElement | null>(null);

const isFileTypeAccepted = (file: File): boolean => {
  if (!props.accept) return true;
  const tokens = props.accept.split(",").map((token) => token.trim().toLowerCase());
  const ext = "." + (file.name.split(".").pop() ?? "").toLowerCase();
  const mime = file.type.toLowerCase();
  return tokens.some((token) => token === ext || token === mime || (token.endsWith("/*") && mime.startsWith(token.slice(0, -1))));
};

const processFiles = (rawFiles: FileList | File[]) => {
  const accepted: File[] = [];
  for (const file of Array.from(rawFiles)) {
    if (!isFileTypeAccepted(file)) {
      emit("error", "type", file);
      continue;
    }
    if (props.maxSize !== undefined && file.size > props.maxSize) {
      emit("error", "size", file);
      continue;
    }
    accepted.push(file);
  }
  if (accepted.length > 0) {
    files.value = props.multiple ? [...files.value, ...accepted] : [accepted[0]];
    emit("update:files", files.value);
  }
};

const handleChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target.files?.length) processFiles(target.files);
};

const handleDrop = (event: DragEvent) => {
  isDragging.value = false;
  if (props.disabled) return;
  const dropped = event.dataTransfer?.files;
  if (dropped?.length) processFiles(dropped);
};

const openFilePicker = () => {
  if (!props.disabled) inputRef.value?.click();
};

const handleKeyDown = (event: KeyboardEvent) => {
  if ((event.key === "Enter" || event.key === " ") && !props.disabled) {
    event.preventDefault();
    inputRef.value?.click();
  }
};

const removeFile = (index: number) => {
  files.value = files.value.filter((_, fileIndex) => fileIndex !== index);
  emit("update:files", files.value);
  if (inputRef.value) inputRef.value.value = "";
};
</script>

<template>
  <div
    role="button"
    :tabindex="disabled ? -1 : 0"
    :aria-label="label"
    :aria-disabled="disabled ? true : undefined"
    class="rounded-16 duration-fast ease-standard relative flex min-h-44 w-full flex-col items-center justify-center border-4 border-dashed transition-colors"
    :class="[
      disabled
        ? 'cursor-not-allowed border-slate-200 opacity-60'
        : isDragging
          ? 'border-app-blue-600 bg-app-blue-50 cursor-copy'
          : errorMsg
            ? 'border-app-red-500 cursor-pointer'
            : 'hover:border-app-blue-600 cursor-pointer border-slate-200',
    ]"
    @click="openFilePicker"
    @dragover.prevent="!disabled && (isDragging = true)"
    @dragleave="isDragging = false"
    @drop.prevent="handleDrop"
    @keydown="handleKeyDown">
    <input
      ref="inputRef"
      type="file"
      :accept="accept"
      :multiple="multiple"
      :disabled="disabled"
      aria-hidden="true"
      tabindex="-1"
      class="sr-only"
      @change="handleChange" />

    <!-- Empty state -->
    <template v-if="files.length === 0">
      <font-awesome-icon
        icon="fa-solid fa-file-arrow-up"
        class="icon-lg text-type-muted mb-px-8" />
      <p class="t-small text-type-muted px-px-16 text-center">
        {{ label }}
      </p>
      <p
        v-if="accept"
        class="t-xs text-type-muted mt-px-4">
        {{ accept.replace(/,\s*/g, ", ").toUpperCase() }}
      </p>
      <p
        v-if="maxSize"
        class="t-xs text-type-muted">
        Max {{ Math.round(maxSize / 1024 / 1024) }} MB
      </p>
      <p
        v-if="errorMsg"
        class="t-xs text-app-red-500 mt-px-8 px-px-16 text-center">
        {{ errorMsg }}
      </p>
    </template>

    <!-- File previews -->
    <div
      v-else
      class="gap-px-8 p-px-16 flex flex-wrap items-center justify-center">
      <div
        v-for="(previewFile, index) in files"
        :key="previewFile.name + index"
        class="rounded-16 shadow-card bg-slate-0 relative flex h-32 w-32 flex-col items-center justify-between border border-slate-200">
        <button
          type="button"
          :aria-label="`Remove ${previewFile.name}`"
          class="text-type-muted hover:text-app-red-500 focus-ring z-dropdown duration-fast ease-standard absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-slate-200 transition-colors"
          @click.stop="removeFile(index)">
          <font-awesome-icon
            icon="fa-solid fa-xmark"
            class="icon-xs" />
        </button>
        <div></div>
        <slot
          name="preview"
          :file="previewFile">
          <font-awesome-icon
            icon="fa-solid fa-file"
            class="icon-xl text-type-muted" />
        </slot>
        <div class="t-xs text-type-muted p-px-8 w-full truncate text-center">
          {{ previewFile.name }}
        </div>
      </div>
    </div>
  </div>
</template>
